import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from '@com/core/common/translations/i18n';
import { CartContext } from '@com/core/common/contexts/cartContextDaily';
import { AppContext } from '@com/core/common/contexts/appContext';
import { CDN_IMG_BASE } from '@com/core/common/baseConfig';
import Switch from './Switch';

type TProps = {
    children?: React.ReactNode;
};

const ExperienceSwitch = ({ children }: TProps): JSX.Element | null => {
    const { t } = useTranslation('common');
    const { locale } = useContext(AppContext);
    const { cart, switchExperience } = useContext(CartContext);
    const [showModal, setShowModal] = useState(false);

    if (!cart || !cart.isExperienceSwitchEnabled) return null;
    const isActive = cart.experience === 'auto-checkout';

    const handleSwitchExperience = () => {
        const newExperience = cart.experience === 'auto-checkout' ? 'manual-checkout' : 'auto-checkout';
        switchExperience(newExperience);
        setShowModal(false);
    };

    const imgName = cart.experience === 'auto-checkout' ? 'manual' : 'auto';

    return (
        <>
            {/* children is for that one banner usage on the home page, otherwise just show the normal switch k */}
            {children ? (
                <CustomSwitchButton type="button" onClick={() => setShowModal(true)}>
                    {children}
                </CustomSwitchButton>
            ) : (
                <SwitchButton className={isActive ? 'active' : ''} type="button" onClick={() => setShowModal(true)}>
                    <img src={`${CDN_IMG_BASE}/simpleIcons/good-morning-mode.svg`} alt="Toggle Good Morning Mode" />
                    {t('good-morning-mode')}
                    <Switch active={isActive} />
                </SwitchButton>
            )}

            {showModal && (
                <Modal>
                    <div className="scrim" onClick={() => setShowModal(false)} aria-hidden="true" />
                    <ModalContent>
                        <header>
                            <p className="title">{t('switch-your-delivery-experience')}</p>
                            <p className="subtitle">{!isActive ? t('_gmm-switch-on') : t('_gmm-switch-off')}</p>
                        </header>
                        {/* cli = use preloeader to stop reflow */}
                        <div className="content">
                            <img
                                src={`${CDN_IMG_BASE}/images/experience-${imgName}-content-${locale.lang}.png`}
                                alt=""
                            />
                        </div>

                        {/* // CLI - add loader here? */}
                        <button type="button" onClick={handleSwitchExperience}>
                            <span>{t('confirm-switch')}</span>
                        </button>
                    </ModalContent>
                </Modal>
            )}
        </>
    );
};

const CustomSwitchButton = styled.button`
    display: block;
`;

const SwitchButton = styled.button`
    background-color: ${({ theme: { colors } }): string => colors.bgSecondary};
    border-radius: ${({ theme }): string => theme.metrics.borderRadius};
    padding: 12px 12px 12px 10px;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    font-size: 0.875rem;

    img {
        ${({ theme: { right } }): string => `margin-${right}: 12px`};
        ${({ theme }): string => `border-${theme.right}: 1px solid ${theme.colors.border}`};
        ${({ theme: { right } }): string => `padding-${right}: 10px`};
    }

    &.active {
        font-weight: bold;
    }

    &:hover {
        .switchToggle {
            /* external component */
            opacity: 0.7;
        }
    }
`;

const Modal = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;

    .scrim {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(255, 255, 255, 0.8);
    }
`;

const ModalContent = styled.div`
    width: 70%;
    max-width: 600px;
    border: 1px solid ${({ theme: { colors } }): string => colors.border};
    border-radius: ${({ theme: { metrics } }): string => metrics.borderRadius};
    position: relative; /* z-index issues */
    background-color: ${({ theme: { colors } }): string => colors.bgPrimary};
    text-align: center;

    header {
        border-bottom: 1px solid ${({ theme: { colors } }): string => colors.borderLight};
        padding: 24px;
    }

    .title {
        font-weight: bold;
        font-size: 1.25rem;
        margin-bottom: 3px;
    }

    .content {
        padding: 24px;
    }

    button {
        width: 100%;
        background-color: ${({ theme: { colors } }): string => colors.brandPrimary};
        color: ${({ theme: { colors } }): string => colors.bodyAlt};
        border-radius: 0 0 ${({ theme: { metrics } }): string => metrics.borderRadius}
            ${({ theme: { metrics } }): string => metrics.borderRadius};
        height: 52px;
        position: relative;
        overflow: hidden;

        &:after {
            content: '';
            position: absolute;
            bottom: 0;
            ${({ theme: { left } }): string => `${left}: 0`};
            width: 0%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.15);
            transition: all ease-in-out 0.2s;
            z-index: 1;
        }

        &:hover {
            &:after {
                width: 100%;
            }
        }

        span {
            font-weight: bold;
            font-size: 18px; /* purposely px */
            position: relative;
            z-index: 2; /* for hover effect to work "behind" it */
        }
    }
`;

export default ExperienceSwitch;
